import { Link } from "react-router-dom";
import { useSEO } from "@/hooks/useSEO";
import { Reveal, RevealStagger, RevealItem } from "@/components/shared/Reveal";
import { ChapterHeading } from "@/components/shared/ChapterHeading";
import { StatStrip } from "@/components/shared/StatStrip";
import { IconTile } from "@/components/shared/IconTile";
import { PhotoCarousel } from "@/components/shared/PhotoCarousel";
import { GALLERY_IMAGES, FEATURED_EDITIONS, COLORS } from "@/data/siteData";

export default function Gallery() {
  useSEO(
    "Gallery & Past Editions | The Market Place Jaipur",
    "Browse photos from past editions of The Market Place — Jaipur's community-led marketplace for homegrown businesses."
  );

  return (
    <div data-testid="gallery-page">
      <section className="pt-16 pb-12 px-6 sm:px-8 max-w-4xl mx-auto text-center">
        <Reveal>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#D81B60]">Gallery</span>
          <h1 className="font-extrabold tracking-tight text-4xl sm:text-5xl lg:text-6xl mt-4 leading-tight">
            Seven Years. Hundreds of Markets. Thousands of Stories.
          </h1>
          <p className="mt-6 text-lg text-gray-600">
            Every edition of The Market Place brings together a different mix of businesses and people, while
            continuing the same community that began in 2019.
          </p>
        </Reveal>
      </section>

      {/* Our Markets */}
      <section className="pb-16 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <p className="text-gray-500 leading-relaxed">
            From recurring markets to festive editions, evening formats and special collaborations, The Market Place
            has continued to evolve with its community. 400+ markets have created thousands of interactions between
            entrepreneurs and customers across Jaipur.
          </p>
        </Reveal>
      </section>

      {/* Featured Editions */}
      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-6xl mx-auto">
        <ChapterHeading number="01" title="Featured Editions" />
        <RevealStagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {FEATURED_EDITIONS.map((e, i) => (
            <RevealItem key={e.title}>
              <div className="rounded-2xl border border-gray-200 p-6 h-full hover:shadow-lg transition-shadow duration-300">
                <IconTile icon={e.icon} title={e.title} desc={e.desc} color={COLORS[i % COLORS.length]} />
              </div>
            </RevealItem>
          ))}
        </RevealStagger>
      </section>

      {/* Gallery — auto-moving photo carousel, no categorisation */}
      <section className="py-12 sm:py-16 max-w-full mx-auto">
        <div className="px-6 sm:px-8 max-w-7xl mx-auto">
          <ChapterHeading number="02" title="The Gallery" subtitle="Hover to pause, drag your cursor away to keep the memories rolling." />
        </div>
        <div data-testid="gallery-grid" className="space-y-4">
          <PhotoCarousel images={GALLERY_IMAGES.slice(0, Math.ceil(GALLERY_IMAGES.length / 2))} direction="left" speed={32} />
          <PhotoCarousel images={GALLERY_IMAGES.slice(Math.ceil(GALLERY_IMAGES.length / 2))} direction="right" speed={38} />
        </div>
      </section>

      <section className="py-16 border-y border-gray-200 bg-[#FDFBF7]">
        <StatStrip />
      </section>

      <section className="py-12 sm:py-16 px-6 sm:px-8 max-w-3xl mx-auto text-center">
        <Reveal>
          <h2 className="font-extrabold text-3xl sm:text-4xl mb-4">Every Market Adds Something to the Story.</h2>
          <p className="text-gray-500 mb-8">New businesses. New customers. New relationships. The same community growing together.</p>
          <Link to="/upcoming-market" data-testid="gallery-view-upcoming-button" className="inline-block rounded-full bg-[#D81B60] text-white px-8 py-4 text-sm font-semibold hover:bg-[#AD144B] transition-colors duration-300">
            View Upcoming Markets
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
